<template>
  <transition name="msgbox-fade">
    <div class="sj-msgbox-wrapper" v-show="visibleData">
      <div class="sj-msgbox" :style="{ width: width }">
        <!-- header，显示标题和图标 -->
        <div class="sj-msgbox-header" v-if="showHeader">
          <i class="sj-msgbox-icon icon_com comalert_warning"></i>
          <span class="sj-msgbox-title">{{ title }}</span>
        </div>
        <!-- body，显示内容或在prompt模式下显示input -->
        <div
          class="sj-msgbox-body"
          :style="{
            'justify-content': type === 'prompt' ? 'flex-start' : 'center'
          }"
        >
          <div class="sj-msgbox-body-message">{{ message }}</div>
          <sj-input
            class="sj-msgbox-body-input"
            v-if="type === 'prompt'"
            border
            v-model="input"
            @keyup.enter="handleConfirm"
            ref="input"
            :placeholder="placeholder"
          />
        </div>
        <!-- footer，显示确定和取消按钮 -->
        <div class="sj-msgbox-footer">
          <div
            class="sj-msgbox-footer-button"
            v-if="type === 'confirm' || type === 'prompt'"
            :disabled="btnDisabled"
            @click="handleClose"
          >
            {{ cancelText }}
          </div>
          <div
            class="sj-msgbox-footer-button"
            :disabled="btnDisabled"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * messagebox组件，使用了utils中的popup mixin作为基础
 * 此弹窗为屏蔽式弹窗，用于用户确认操作等场合，并根据用户确认或取消做出响应
 *
 * 组件注册至vue实例，参见index.js
 */
import Popup from "../../../utils/popup/index.js";

export default {
  name: "SjMessageBox",
  mixins: [Popup],
  data() {
    return {
      width: "360px",
      title: "",
      message: "",
      type: "",
      showHeader: true,

      confirmText: "确定",
      cancelText: "取消",
      onClose: null,
      onCloseConfirmText: "确定",

      resolve: null,
      reject: null,
      input: "",
      placeholder: "",
      validator: null,
      btnDisabled: false,

      /**
       * 内部变量，不作为配置项
       */
      visibleData: false,
      closed: false
    };
  },
  methods: {
    /**
     * handleClose - 点击取消的方法
     */
    async handleClose() {
      try {
        /**
         * 尝试调用onClose（如果存在）
         */
        if (typeof this.onClose === "function") {
          this.confirmText = this.onCloseConfirmText;
          this.btnDisabled = true;
          await this.onClose();
          this.confirmText = "确定";
          this.btnDisabled = false;
        }
        /**
         * 尝试调用reject（如果存在）
         */
        if (typeof this.reject === "function") {
          this.reject("USER CANCEL");
        }
        this.closed = true;
      } catch (e) {
        console.log(e);
      }
    },
    /**
     * handleConfirm - 点击确定的方法
     */
    async handleConfirm() {
      try {
        /**
         * prompt模式传入validator时检测
         */
        if (this.validator && !this.validator(this.input)) {
          return;
        }
        /**
         * 尝试调用onClose（如果存在）
         */
        if (typeof this.onClose === "function") {
          this.confirmText = this.onCloseConfirmText;
          this.btnDisabled = true;
          await this.onClose(this.input, this);
          this.confirmText = "确定";
          this.btnDisabled = false;
        }
        /**
         * 尝试调用resolve（如果存在）
         */
        if (typeof this.resolve === "function") {
          this.resolve(this.input);
        }
        this.closed = true;
      } catch (e) {
        console.log(e);
      }
    },
    destroyElement() {
      this.$destroy(true);
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  mounted() {
    /**
     * prompt模式自动选中input
     */
    if (this.type === "prompt") {
      setTimeout(() => {
        this.$refs.input.focus();
      }, 0);
    }
  },
  watch: {
    /**
     * 完成操作时销毁MessageBox
     */
    closed(newVal) {
      if (newVal) {
        this.visibleData = false;
        this.destroyElement();
      }
    }
  }
};
</script>

<style lang="less">
@msgbox-background-color: @color-antibg;
@msgbox-background-color-wrapper: rgba(0, 0, 0, 0.5);
@msgbox-box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.06);
@msgbox-border-radius: @base-border-radius;
@msgbox-border: @base-border-width @base-border-style @color-gray-2;
@msgbox-header-item-space: 10px;

@msgbox-header-padding: 40px;
@msgbox-header-icon-color: @color-alert-error;

@msgbox-body-padding-horizontal: 55px;
@msgbox-body-padding-vertical: 20px;

@msgbox-hover-color: @color-gray-3;

@msgbox-close-font-size: 16px;

.msgbox-fade-enter-active {
  animation: msgbox-fade-in 0.3s;
}

.msgbox-fade-leave-active {
  animation: msgbox-fade-out 0.3s;
}

@keyframes msgbox-fade-in {
  0% {
    transform: translate3d(0, -20px, 0);
    opacity: 0;
  }
  100% {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

@keyframes msgbox-fade-out {
  0% {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
  100% {
    transform: translate3d(0, -20px, 0);
    opacity: 0;
  }
}

.sj-msgbox-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0;
  background-color: @msgbox-background-color-wrapper;
  display: flex;
  align-items: center;
  justify-content: center;

  .sj-msgbox {
    position: relative;
    background-color: @msgbox-background-color;
    border-radius: @msgbox-border-radius;
    box-shadow: @msgbox-box-shadow;
    border: @msgbox-border;
    box-sizing: border-box;

    .sj-msgbox-header {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: @msgbox-header-padding;

      .sj-msgbox-icon {
        .text("h3");
        color: @msgbox-header-icon-color;
        margin-right: @msgbox-header-item-space;
      }

      .sj-msgbox-title {
        .text("h3", "dark", "semibold");
      }
    }

    .sj-msgbox-body {
      .text("body", "dark");
      display: flex;
      align-items: center;
      padding: @msgbox-body-padding-vertical @msgbox-body-padding-horizontal;

      .sj-msgbox-body-message {
        flex-shrink: 0;
        max-width: 100%;
        word-break: break-all;
        text-align-last: center;
      }
      .sj-msgbox-body-input {
        margin-left: @msgbox-header-item-space;
        min-width: 0;
      }
    }

    .sj-msgbox-footer {
      height: 50px;
      box-sizing: border-box;
      border-top: @msgbox-border;
      display: flex;

      .sj-msgbox-footer-button {
        .text("body", "available");
        cursor: pointer;
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .sj-msgbox-footer-button:hover {
        background-color: @msgbox-hover-color;
      }
      .sj-msgbox-footer-button:active {
        background-color: @msgbox-hover-color;
      }
      .sj-msgbox-footer-button:nth-child(2) {
        border-left: @msgbox-border;
      }
    }
  }
}
</style>
